<template>
    <div>
        <PinConfirmationDialog
            :is-open="pinConfirmationDialog"
            :pin-data="pin"
            @confirm-pin="confirmPin"
            @on-close="closePinConfirmationDialog"
        />

        <PackageAmountDialog
            :is-open="packageAmountDialog"
            :address-data="currentAddress"
            @update-sending="updateSendingParameters"
            @on-close="closePackageAmountDialog"
        />

        <AmountToShipDialog
            :is-open="amountToShipDialog"
            :address-data="currentAddress"
            @update-sending="updateSendingParameters"
            @reset-recalculate-unit-box="resetRecalculateUnitBox"
            @on-close="closeAmountToShipDialog"
        />

        <CourierDialog
            :is-open="courierDialog"
            :address-data="currentAddress"
            :couriers="couriers"
            @update-sending="updateSendingParameters"
            @on-close="closeCourierDialog"
        />

        <ReprintLabelsDialog
            :is-open="reprintLabelsDialog"
            :address-data="currentAddress"
            @reprint-labels="reprintLabels"
            @on-close="closeReprintLabelsDialog"
        />
        <v-container class="pa-0">
            <v-row v-if="isOrderFound" dense>
                <template>
                    <v-col cols="12">
                        <v-alert
                            v-if="order.shippingBlocked"
                            type="info"
                            class="mb-0"
                            elevation="2"
                            text
                            border="left"
                            icon="mdi-information-outline"
                        >
                            Wysyłka zablokowana. Zostanie wygenerowana etykieta tymczasowa.
                        </v-alert>
                    </v-col>
                    <v-col>
                        <v-btn
                            v-if="hasOrderComments && !areOrderCommentsVisible"
                            color="error"
                            outlined
                            x-large
                            block
                            class="mb-3"
                            data-sentry-label="Pokaż uwagi do zamówienia"
                            @click="areOrderCommentsVisible = true"
                        >
                            Pokaż uwagi do zamówienia
                        </v-btn>

                        <v-alert
                            v-if="hasOrderComments"
                            v-model="areOrderCommentsVisible"
                            type="error"
                            prominent
                            dark
                            elevation="2"
                        >
                            <span class="text-uppercase">Uwagi do zamówienia</span>

                            <v-divider
                                class="my-4"
                                color="error"
                            />

                            <div v-for="(comment, index) in order.orderComments" :key="index">
                                {{ index + 1 }}. {{ comment }}
                            </div>

                            <v-divider
                                class="my-4"
                                color="error"
                            />
                            <v-btn
                                depressed
                                outlined
                                large
                                block
                                class="mt-5"
                                data-sentry-label="Potwierdzenie zapoznania się z komentarzem"
                                @click="confirmOrderCommentsRead()"
                            >
                                Potwierdzam zapoznanie się z komentarzem
                            </v-btn>
                        </v-alert>
                        <v-alert
                            v-if="isCashOnDeliveryPartialSent"
                            type="warning"
                            icon="mdi-alert"
                            border="left"
                            class="alertText"
                        >
                            Zamówienie nadane za pobraniem. Jeżeli chcesz nadać, zrób to ręcznie.
                        </v-alert>
                        <v-alert
                            v-if="isStandardCooperantOrder"
                            type="warning"
                            icon="mdi-alert"
                            border="left"
                            class="alertText"
                        >
                            To jest zamówienie wykonywane w kooperacji. Nadanie na linii nie jest możliwe.
                        </v-alert>
                        <v-carousel
                            v-model="addressCarouselIndex"
                            height="auto"
                            hide-delimiters
                            :continuous="false"
                            :dark="$vuetify.theme.dark"
                            :light="!$vuetify.theme.dark"
                            :show-arrows="order.addresses.length > 1"
                        >
                            <v-carousel-item
                                v-for="(address, index) in order.addresses"
                                :key="address.id"
                            >
                                <v-toolbar
                                    :dark="address.orderAddress.isSent"
                                    flat
                                    height="30"
                                    :color="address.orderAddress.isSent ? 'success' : 'grey lighten-2'"
                                >
                                    <v-row justify="center">
                                        Adres {{ index + 1 }} z {{ order.addresses.length }}
                                    </v-row>
                                </v-toolbar>
                                <v-container class="px-0">
                                    <v-row>
                                        <v-col v-if="!address.orderAddress.isPallet" cols="10" offset="1">
                                            <h2 class="text-h5 mb-4">
                                                {{ address.company }} {{ address.firstName }} {{ address.lastName }}, {{ address.street }}, {{ address.postCode }} {{ address.city }}, {{ address.country }}
                                            </h2>

                                            <div class="d-inline-flex mr-3">
                                                <p class="my-0">
                                                    Status:
                                                    <strong :class="orderAddressSendStatusClass(index)">
                                                        {{ address.orderAddress.orderAddressStatus.name }}
                                                    </strong>
                                                </p>
                                            </div>

                                            <div class="d-inline-flex ml-3 mr-2">
                                                <p class="d-flex align-center my-0">
                                                    Ilość kartonów: <strong class="mx-1">{{ packageAmountSum(address) }}</strong>
                                                    <v-btn
                                                        class="ml-1"
                                                        icon
                                                        color="primary"
                                                        small
                                                        data-sentry-label="Edytuj ilość kartonów"
                                                        @click="canApplyChangesWithoutPin ? showPackageAmountDialog(currentAddress) : showPinDialog('packageAmount')"
                                                    >
                                                        <v-icon small>
                                                            mdi-pencil
                                                        </v-icon>
                                                    </v-btn>
                                                </p>
                                            </div>

                                            <div class="d-inline-flex mx-2">
                                                <p class="d-flex align-center my-0">
                                                    Nakład: <strong class="mx-1">{{ address.orderAddress.amountToShip }} szt.</strong>
                                                    <v-btn
                                                        class="ml-1"
                                                        icon
                                                        color="primary"
                                                        small
                                                        data-sentry-label="Edytuj nakład"
                                                        @click="showAmountToShipDialog(currentAddress)"
                                                    >
                                                        <v-icon small>
                                                            mdi-pencil
                                                        </v-icon>
                                                    </v-btn>
                                                </p>
                                            </div>

                                            <div class="d-inline-flex ml-2 mr-3">
                                                <p class="my-0">
                                                    Waga: <strong>{{ formatWeight(address.orderAddress.weight) }}</strong>
                                                </p>
                                            </div>

                                            <div class="d-inline-flex ml-3">
                                                <p class="d-flex align-center">
                                                    Kurier: <strong class="mx-1">{{ address.orderAddress.carrier.shortCode }}</strong>
                                                    <v-btn
                                                        class="ml-1"
                                                        icon
                                                        color="primary"
                                                        small
                                                        data-sentry-label="Zmień kuriera"
                                                        @click="canApplyChangesWithoutPin ? showCourierDialog(currentAddress) : showPinDialog('courier')"
                                                    >
                                                        <v-icon small>
                                                            mdi-pencil
                                                        </v-icon>
                                                    </v-btn>
                                                </p>
                                            </div>

                                            <v-container v-if="!hasCollectingBox" class="pa-0 mt-5">
                                                <v-row>
                                                    <v-col cols="12" md="6">
                                                        <v-card>
                                                            <v-badge
                                                                :value="hover"
                                                                color="pink"
                                                                content="Dane pobrane ze sklepu"
                                                                overlap
                                                                right
                                                                transition="slide-x-transition"
                                                            >
                                                                <v-hover v-model="hover" :disabled="!address.orderAddress.isPmData">
                                                                    <v-list>
                                                                        <v-list-item>
                                                                            <v-list-item-content>
                                                                                <v-row no-gutters dense>
                                                                                    <v-col cols="12" sm="7">
                                                                                        <strong>Typ kartonu</strong>
                                                                                    </v-col>
                                                                                    <v-col cols="12" sm="5">
                                                                                        <strong>Ilość</strong>
                                                                                    </v-col>
                                                                                </v-row>
                                                                            </v-list-item-content>
                                                                        </v-list-item>
                                                                        <v-divider />
                                                                        <v-list-item v-for="(box, boxIndex) in address.orderAddress.boxesInfo" :key="boxIndex">
                                                                            <v-list-item-content>
                                                                                <v-row no-gutters align="center" dense>
                                                                                    <v-col cols="12" sm="7">
                                                                                        {{ box.packageType }}
                                                                                    </v-col>
                                                                                    <v-col cols="12" sm="5">
                                                                                        <v-text-field
                                                                                            v-model="box.packageAmount"
                                                                                            outlined
                                                                                            readonly
                                                                                            dense
                                                                                            hide-details
                                                                                            prepend-icon="mdi-minus"
                                                                                            append-outer-icon="mdi-plus"
                                                                                            @click:prepend="decreaseBoxAmount(address, boxIndex)"
                                                                                            @click:append-outer="increaseBoxAmount(address, boxIndex)"
                                                                                        />
                                                                                    </v-col>
                                                                                </v-row>
                                                                            </v-list-item-content>
                                                                        </v-list-item>
                                                                        <v-divider />
                                                                        <v-list-item>
                                                                            <v-list-item-content>
                                                                                <v-row no-gutters dense>
                                                                                    <v-col cols="12" sm="7" class="text-right">
                                                                                        <strong>Suma:</strong>
                                                                                    </v-col>
                                                                                    <v-col cols="12" offset-sm="" sm="5" class="text-center">
                                                                                        {{ packageAmountSum(address) }}
                                                                                    </v-col>
                                                                                </v-row>
                                                                            </v-list-item-content>
                                                                        </v-list-item>
                                                                    </v-list>
                                                                </v-hover>
                                                            </v-badge>
                                                        </v-card>
                                                    </v-col>
                                                </v-row>
                                            </v-container>

                                            <v-container v-else class="pa-0 mt-5">
                                                <v-row>
                                                    <v-col cols="12">
                                                        <v-card>
                                                            <v-list>
                                                                <v-list-item>
                                                                    <v-list-item-content>
                                                                        <v-row no-gutters dense>
                                                                            <v-col cols="12" sm="3">
                                                                                <strong>Typ kartonu</strong>
                                                                            </v-col>
                                                                            <v-col cols="12" sm="3">
                                                                                <strong>Ilość</strong>
                                                                            </v-col>
                                                                            <v-col cols="12" sm="3">
                                                                                <strong>Typ zbiorczego</strong>
                                                                            </v-col>
                                                                            <v-col cols="12" sm="3">
                                                                                <strong>Ilość</strong>
                                                                            </v-col>
                                                                        </v-row>
                                                                    </v-list-item-content>
                                                                </v-list-item>

                                                                <v-divider />

                                                                <v-list-item v-for="(box, boxIndex) in address.orderAddress.boxesInfo" :key="boxIndex">
                                                                    <v-list-item-content>
                                                                        <v-row no-gutters align="center" dense class="mb-2">
                                                                            <v-col cols="12" sm="3">
                                                                                {{ box.packageType }}
                                                                            </v-col>
                                                                            <v-col cols="12" sm="3">
                                                                                <v-text-field
                                                                                    v-model="box.packageAmount"
                                                                                    outlined
                                                                                    class="mr-3"
                                                                                    readonly
                                                                                    dense
                                                                                    hide-details
                                                                                    prepend-icon="mdi-minus"
                                                                                    append-outer-icon="mdi-plus"
                                                                                    @click:prepend="decreaseBoxAmount(address, boxIndex)"
                                                                                    @click:append-outer="increaseBoxAmount(address, boxIndex)"
                                                                                />
                                                                            </v-col>
                                                                            <v-col cols="12" sm="3">
                                                                                {{ box.collectingBox.packageType }}
                                                                            </v-col>
                                                                            <v-col cols="12" sm="3">
                                                                                {{ box.collectingBox.packageAmount }}
                                                                            </v-col>
                                                                        </v-row>
                                                                        <v-divider />
                                                                    </v-list-item-content>
                                                                </v-list-item>
                                                            </v-list>
                                                        </v-card>
                                                    </v-col>
                                                </v-row>
                                            </v-container>
                                            <v-container class="pa-0 mt-5">
                                                <v-row>
                                                    <v-col cols="12">
                                                        <v-alert
                                                            v-for="(gift) in address.orderAddress.orderAddressGifts"
                                                            :key="gift.id"
                                                            dense
                                                            outlined
                                                            border="left"
                                                            type="error"
                                                        >
                                                            {{ gift.userInfo }}
                                                        </v-alert>
                                                    </v-col>
                                                </v-row>
                                            </v-container>

                                            <v-container class="pa-0">
                                                <v-row justify="center">
                                                    <v-btn
                                                        v-if="hasUnitBoxChanged"
                                                        class="mx-2"
                                                        min-width="215"
                                                        large
                                                        depressed
                                                        color="primary"
                                                        data-sentry-label="Przelicz"
                                                        :loading="actionLoading.recalculateUnitBox"
                                                        @click="recalculateUnitBox(address)"
                                                    >
                                                        <v-icon left dark>
                                                            mdi-calculator
                                                        </v-icon>
                                                        Przelicz
                                                    </v-btn>
                                                    <v-btn
                                                        v-if="!hasUnitBoxChanged && !address.orderAddress.isSent"
                                                        class="mx-2"
                                                        min-width="215"
                                                        large
                                                        depressed
                                                        data-sentry-label="Wydrukuj etykiety"
                                                        :color="isTemporaryLabelSelect ? 'info' : 'success'"
                                                        :loading="actionLoading.printLabels"
                                                        :outlined="isTemporaryLabelSelect"
                                                        :disabled="(hasOrderComments && areOrderCommentsVisible) || (!isTemporaryLabelSelect && order.shippingBlocked) || order.isMultiProject
                                                            || address.orderAddress.amountToShip === 0 || isCashOnDeliveryPartialSent || isStandardCooperantOrder"
                                                        @click="printLabels(address)"
                                                    >
                                                        {{ isTemporaryLabelSelect ? 'Wydrukuj' : 'Nadaj' }}
                                                    </v-btn>
                                                    <v-btn
                                                        v-if="!hasUnitBoxChanged && (address.orderAddress.isSent || address.orderAddress.isPartialShipping)"
                                                        class="mx-2"
                                                        min-width="215"
                                                        large
                                                        color="primary"
                                                        depressed
                                                        outlined
                                                        data-sentry-label="Wydrukuj ponownie"
                                                        :loading="actionLoading.reprintLabels"
                                                        :disabled="order.isMultiProject"
                                                        @click="canApplyChangesWithoutPin ? showReprintLabelsDialog() : showPinDialog('reprintLabels')"
                                                    >
                                                        Wydrukuj ponownie
                                                    </v-btn>
                                                    <v-btn
                                                        v-if="!hasUnitBoxChanged && address.orderAddress.isSent"
                                                        class="mx-2"
                                                        min-width="215"
                                                        large
                                                        color="primary"
                                                        depressed
                                                        data-sentry-label="Generuj nowe"
                                                        :disabled="actionLoading.reprintLabels || address.orderAddress.amountToShip === 0 || isCashOnDeliveryPartialSent || isStandardCooperantOrder"
                                                        :loading="actionLoading.printLabels"
                                                        @click="canApplyChangesWithoutPin ? printLabels(address) : showPinDialog('generateNewLabels')"
                                                    >
                                                        Generuj nowe
                                                    </v-btn>
                                                </v-row>
                                            </v-container>
                                        </v-col>
                                        <v-col v-else cols="10" offset="1">
                                            <v-alert
                                                type="info"
                                                text
                                                border="left"
                                            >
                                                Przesyłka wysyłana paletą.
                                            </v-alert>
                                        </v-col>
                                    </v-row>
                                </v-container>
                            </v-carousel-item>
                        </v-carousel>
                    </v-col>
                </template>
            </v-row>
            <v-row v-else>
                <v-col cols="12">
                    <v-alert
                        type="info"
                        text
                        border="left"
                    >
                        Brak pakowania.
                    </v-alert>
                </v-col>
            </v-row>
        </v-container>
        <v-row no-gutters justify="space-between">
            <v-col cols="auto">
                <v-btn
                    color="primary"
                    outlined
                    class="mt-3 mb-2"
                    data-sentry-label="Wstecz"
                    @click="showPreviousStep"
                >
                    Wstecz
                </v-btn>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import PinConfirmationDialog from '@/views/Shipments/Sendings/PinConfirmationDialog'
import PackageAmountDialog from '@/views/Shipments/Sendings/PackageAmountDialog'

import CourierDialog from '@/views/Shipments/Sendings/CourierDialog'
import AmountToShipDialog from '@/views/Shipments/Sendings/AmountToShipDialog'
import ReprintLabelsDialog from '@/views/Shipments/Sendings/ReprintLabelsDialog'

import { appConfig } from '@/config'

import { errorHandler, addSentryBreadcrumb } from '@/utils/errorHandler'
import { hasUserPermission } from '@/utils/permissionsHandler'
import { EventBus } from '@/utils/eventBus'

import weightFormatter from '@/mixins/weightFormatter'

export default {
    name: 'SendingData',
    components: {
        PinConfirmationDialog,
        PackageAmountDialog,
        CourierDialog,
        AmountToShipDialog,
        ReprintLabelsDialog,
    },
    mixins:[
        weightFormatter
    ],
    props: {
        orderNumber: {
            type: String,
            default: null
        },
        reportData: {
            type: Object,
            default: null
        },
        workspaceId: {
            type: Number,
            default: null
        },
        couriers: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            report: {
                statusId: null,
                objectDetail: {
                    reportItemPlateParameters: []
                },
                comments: []
            },
            actionLoading: {
                getOrder: false,
                printLabels: false,
                reprintLabels: false,
                recalculateUnitBox: false
            },
            areOrderCommentsVisible: true,
            addressCarouselIndex: 0,
            hover: false,
            couriersListDialog: false,
            hasUserHigherPermissions: false,
            pin: appConfig.sendings.permissionsPin,
            pinConfirmationAction: null,
            pinConfirmationDialog: false,
            amountToReportDialog: false,
            courierDialog: false,
            packageAmountDialog: false,
            amountToShipDialog: false,
            currentAddress: {
                orderAddress: {
                    carrier: {},
                    packageAmount: null
                }
            },
            reprintLabelsDialog: false,
            hasUnitBoxChanged: false,
            order: {
                orderFullNumberCFPrint: null,
                orderProductType: null,
                orderTotalAmount: null,
                orderProductFormat: null,
                orderComments: [],
                packageId: null,
                addresses: [],
                isSent: false,
                cashOnDeliveryPartialSent: false,
                isStandardCooperantOrder: false
            },
            shippingQuantity: null,
            reportCommentHeaders: [
                {
                    text: 'Zdarzenie',
                    value: 'codeType',
                    sortable: false,
                },
                {
                    text: 'Opis',
                    value: 'description',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Data utworzenia',
                    value: 'createdDateTime',
                    sortable: false,
                    align: 'center',
                },
            ]
        }
    },
    computed: {
        hasOrderComments() {
            return this.order.orderComments ? this.order.orderComments.length > 0 : false
        },
        hasCollectingBox() {
            return this.currentAddress && this.currentAddress.orderAddress.boxesInfo.some(item => item.collectingBox !== null)
        },
        isTemporaryLabelSelect() {
            return this.currentAddress && this.currentAddress.orderAddress.carrier.id === 6
        },
        canApplyChangesWithoutPin() {
            return hasUserPermission('sendings_without_pin') || this.hasUserHigherPermissions
        },
        isOrderFound() {
            return this.order.orderFullNumberCFPrint !== null
        },
        isCashOnDeliveryPartialSent() {
            return this.order.cashOnDeliveryPartialSent
        },
        isStandardCooperantOrder() {
            return this.order.isStandardCooperantOrder
        }
    },
    watch: {
        addressCarouselIndex: {
            handler: function(index) {
                this.currentAddress = this.order.addresses[index]
            }
        },
        
    },
    created() {
        EventBus.$on('getOrder', (orderNumber) => { this.getOrder(orderNumber) })
    },
    destroyed() {
        EventBus.$off()
    },
    methods: {
        showPreviousStep() {
            this.$emit('show-previous-step')
        },
        async getOrder(
            orderNumber,
            loader = true,
            resetCarousel = true,
            showComments = true,
            carrierId = null
        ) {
            this.closePinConfirmationDialog()
            this.closeCourierDialog()
            this.actionLoading.getOrder = loader

            let query = `?orderNumber=${orderNumber}`

            if (carrierId) {
                query = `${query}&carrierId=${carrierId}`
            }

            return await this.$store.dispatch('shipments/sendings/getOrder', query )
                .then(response => {
                    if (response.data.isMultiProject) {
                        this.$store.dispatch('showNotification', {
                            type: 'info',
                            message: 'Zamówienie wielowzorowe. Nie można nadać na linii.'
                        })
                    }

                    this.order = response.data
                    this.order.addresses.forEach(address => { address.orderAddress.packageAmountSum = this.packageAmountSum(address) })

                    if (resetCarousel) {
                        setTimeout(() => {
                            this.addressCarouselIndex = 0
                        }, 1) // Carousel FIX
                        this.currentAddress = this.order.addresses[0]
                    } else {
                        this.currentAddress = this.order.addresses[this.addressCarouselIndex]
                    }

                    if (showComments) {
                        this.areCommentsVisible = this.order.addresses.some(address => !address.isSent)
                    } else {
                        this.areCommentsVisible = false
                    }

                    this.order.addresses.forEach(address => {
                        address.orderAddress.boxesInfo.forEach((box, index) => {
                            box.id = index
                        })
                    })
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.getOrder = false
                    this.hasUnitBoxChanged = false
                    this.$emit('order-loading')
                })
        },
        showCouriersListDialog() {
            this.couriersListDialog = true
            addSentryBreadcrumb('ui.action', 'Podgląd listy przewoźników')
        },
        closeCouriersListDialog() {
            this.couriersListDialog = false
        },
        confirmOrderCommentsRead() {
            this.areOrderCommentsVisible = false
        },
        showPinDialog(actionName) {
            this.pinConfirmationAction = actionName
            this.pinConfirmationDialog = true
        },
        closePinConfirmationDialog() {
            this.pinConfirmationAction = null
            this.pinConfirmationDialog = false
        },
        confirmPin() {
            this.pinConfirmationDialog = false

            switch (this.pinConfirmationAction) {
                case 'packageAmount':
                    this.showPackageAmountDialog(this.currentAddress)
                    break
                case 'courier':
                    this.showCourierDialog(this.currentAddress)
                    break
                case 'reprintLabels':
                    this.showReprintLabelsDialog()
                    break
                case 'generateNewLabels':
                    this.printLabels(this.currentAddress)
                    break
            }
        },
        parseAddress(shippingOrderPosition) {
            return shippingOrderPosition.street
                ? `${this.parseEmptyValue(shippingOrderPosition.companyName)}, ${shippingOrderPosition.street}, ${shippingOrderPosition.postCode} ${shippingOrderPosition.city } ${shippingOrderPosition.country }`
                : 'Brak adresu'
        },
        orderAddressSendStatusClass(addressIndex) {
            const statusId = this.order.addresses[addressIndex].orderAddress.orderAddressStatus.id

            switch (statusId) {
                case 10:
                    return 'error--text'
                case 50:
                    return 'warning--text'
                case 100:
                    return 'success--text'
                default:
                    break
            }

            return 'success--text'
        },
        showPackageAmountDialog() {
            this.closePinConfirmationDialog()

            setTimeout(() => {
                this.packageAmountDialog = true
            }, 500)
        },
        closePackageAmountDialog() {
            this.hasUserHigherPermissions = false
            this.packageAmountDialog = false
        },
        showAmountToShipDialog() {
            this.amountToShipDialog = true
        },
        closeAmountToShipDialog() {
            this.amountToShipDialog = false
        },
        showCourierDialog() {
            this.closePinConfirmationDialog()

            setTimeout(() => {
                this.courierDialog = true
            }, 500)
        },
        closeCourierDialog() {
            this.hasUserHigherPermissions = false
            this.courierDialog = false
        },
        updateSendingParameters(address) {
            const index = this.order.addresses.findIndex(item => item.id === address.id)

            this.currentAddress = address
            this.order.addresses.splice(index, 1, address)
        },
        recalculateUnitBox(address) {
            this.actionLoading.recalculateUnitBox = true
            let promises = []

            this.unitBoxesToRecalculate.forEach(unitBox => {
                promises.push(this.$store.dispatch('shipments/sendings/recalculateUnitBox', unitBox))
            })

            Promise.all(promises)
                .then(responses => {
                    responses.forEach(response => {
                        const addressIndex = this.order.addresses.findIndex(address => response.data.addressId === address.id),
                            unitBoxIndex = this.order.addresses[addressIndex].orderAddress.boxesInfo.findIndex(box => response.data.packageTypeId == box.packageTypeId),
                            unitBox = this.order.addresses[addressIndex].orderAddress.boxesInfo[unitBoxIndex]

                        unitBox.packageTypeId = response.data.packageTypeId
                        unitBox.packageAmount = response.data.packageAmount
                        unitBox.totalProductsBoxType = response.data.totalProductsBoxType
                        unitBox.collectingBox = response.data.collectingBox
                        unitBox.totalWeight = response.data.totalWeight
                    })
                    this.resetRecalculateUnitBox()
                    address.orderAddress.packageAmountSum = this.packageAmountSum(address)
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.recalculateUnitBox = false
                })
        },
        resetRecalculateUnitBox() {
            this.unitBox.packageTypeId = null
            this.unitBox.packageAmount = null
            this.unitBox.totalProductsBoxType = null
            this.unitBox.productWeight = null
            this.hasUnitBoxChanged = false
            this.unitBoxesToRecalculate = []
        },
        decreaseBoxAmount(address, boxIndex) {
            if (this.packageAmountSum(address) > 1) {
                const box = address.orderAddress.boxesInfo[boxIndex]

                box.packageAmount > 0 ? box.packageAmount-- : false

                if (box.collectingBox !== null) {
                    this.changeUnitBox(address, box)
                }
            } else {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: 'Osiągnięto minimalną ilość kartonów.'
                })
            }
        },
        increaseBoxAmount(address, boxIndex) {
            if (this.packageAmountSum(address) < address.orderAddress.packageAmountSum) {
                const box = address.orderAddress.boxesInfo[boxIndex]

                box.packageAmount++

                if (box.collectingBox !== null) {
                    this.changeUnitBox(address, box)
                }
            } else {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: 'Osiągnięto maksymalną ilość kartonów.'
                })
            }
        },
        changeUnitBox(address, box) {
            this.hasUnitBoxChanged = true
            this.unitBox.id = box.id
            this.unitBox.packageTypeId = box.packageTypeId
            this.unitBox.packageAmount = box.packageAmount
            this.unitBox.totalProductsBoxType = box.totalProductsBoxType
            this.unitBox.productWeight = address.orderAddress.productWeight
            this.unitBox.addressId = address.id

            const unitBoxIndex = this.unitBoxesToRecalculate.findIndex(item => item.id === box.id),
                unitBox = JSON.parse(JSON.stringify(this.unitBox))

            unitBoxIndex > -1 ? this.unitBoxesToRecalculate.splice(unitBoxIndex, 1, unitBox) : this.unitBoxesToRecalculate.push(unitBox)
        },
        async updateOrderAddress(address) {
            return await this.$store.dispatch('shipments/sendings/updateOrderAddress', address.orderAddress)
                .catch(error => {
                    throw error
                })
        },
        printLabels(address) {
            this.actionLoading.printLabels = true

            this.updateOrderAddress(address)
                .then(() => {
                    this.$store.dispatch('shipments/sendings/printSendingsLabels', {
                        productionSiteId: this.workspaceId,
                        deliveries: [{
                            addressId: address.orderAddress.id,
                            amountToShip: address.orderAddress.amountToShip,
                            carrierId: address.orderAddress.carrier.id,
                            boxesInfo: address.orderAddress.boxesInfo,
                            pallet: false,
                        }]
                    })
                        .then(response => {
                            const carrierId = response.data.carrierId

                            this.getOrder(false, false, false, carrierId)
                                .catch(error => {
                                    errorHandler(error)
                                })
                                .finally(() => {
                                    this.actionLoading.printLabels = false
                                })

                            this.$store.dispatch('showNotification', {
                                type: 'success',
                                message: 'Etykieta została wydrukowana.'
                            })
                        })
                        .catch(error => {
                            this.actionLoading.printLabels = false
                            errorHandler(error)
                        })
                })
                .catch(error => {
                    this.actionLoading.printLabels = false
                    errorHandler(error)
                })
        },
        showReprintLabelsDialog() {
            this.reprintLabelsDialog = true
        },
        closeReprintLabelsDialog() {
            this.hasUserHigherPermissions = false
            this.reprintLabelsDialog = false
        },
        reprintLabels(shipmentIds) {
            this.actionLoading.reprintLabels = true

            this.$store.dispatch('shipments/sendings/reprintSendingsLabels', {
                productionSiteId: this.workspaceId,
                shipments: shipmentIds
            })
                .catch(error => errorHandler(error))
                .finally(() => {
                    this.actionLoading.reprintLabels = false
                })
        },
        packageAmountSum(address) {
            const boxes = address.orderAddress.boxesInfo
            let sum = 0

            boxes.forEach(box => sum += box.packageAmount)

            return sum
        }
    },
}
</script>
